import React, { useState, createContext, useContext } from 'react';
import { HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// SIMPLE TOOLTIP (Legacy - for basic usage with content prop)
// =============================================================================

interface SimpleTooltipProps {
  content: string;
  children?: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

const Tooltip: React.FC<SimpleTooltipProps> = ({ 
  content, 
  children,
  position = 'top' 
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-foreground',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-foreground',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-foreground',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-foreground',
  };

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children || <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />}
      </div>

      {isVisible && (
        <div
          className={`absolute z-50 ${positionClasses[position]} animate-in fade-in-0 zoom-in-95`}
          role="tooltip"
        >
          <div className="bg-popover text-popover-foreground text-xs rounded px-3 py-2 max-w-xs shadow-md border border-border">
            {content}
          </div>
          <div 
            className={`absolute w-0 h-0 border-4 border-transparent ${arrowClasses[position]}`}
          />
        </div>
      )}
    </div>
  );
};

// =============================================================================
// COMPOUND TOOLTIP (Radix-style API for more complex usage)
// =============================================================================

interface TooltipContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const TooltipContext = createContext<TooltipContextValue | null>(null);

const useTooltipContext = () => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('Tooltip components must be used within TooltipProvider');
  }
  return context;
};

// Provider wrapper (can be used at app level or per-tooltip)
const TooltipProvider: React.FC<{ children: React.ReactNode; delayDuration?: number }> = ({ 
  children 
}) => {
  return <>{children}</>;
};

// Root tooltip component
interface TooltipRootProps {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const TooltipRoot: React.FC<TooltipRootProps> = ({ 
  children, 
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange 
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  
  const setIsOpen = (open: boolean) => {
    setInternalOpen(open);
    onOpenChange?.(open);
  };

  return (
    <TooltipContext.Provider value={{ isOpen, setIsOpen }}>
      <div className="relative inline-block">
        {children}
      </div>
    </TooltipContext.Provider>
  );
};

// Trigger element
const TooltipTrigger: React.FC<{ 
  children: React.ReactNode; 
  asChild?: boolean;
  className?: string;
}> = ({ children, className }) => {
  const { setIsOpen } = useTooltipContext();

  return (
    <div
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={() => setIsOpen(true)}
      onBlur={() => setIsOpen(false)}
      className={cn("cursor-pointer", className)}
    >
      {children}
    </div>
  );
};

// Content element
const TooltipContent: React.FC<{ 
  children: React.ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  sideOffset?: number;
}> = ({ children, side = 'top', className }) => {
  const { isOpen } = useTooltipContext();

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        "absolute z-50 animate-in fade-in-0 zoom-in-95",
        positionClasses[side]
      )}
      role="tooltip"
    >
      <div className={cn(
        "bg-popover text-popover-foreground text-xs rounded px-3 py-1.5 shadow-md border",
        className
      )}>
        {children}
      </div>
    </div>
  );
};

// Rename simple tooltip to avoid conflict
const SimpleTooltip = Tooltip;

// Re-export for both usage patterns:
// - Named 'Tooltip' = compound/Radix-style wrapper (most common)
// - Named 'SimpleTooltip' = legacy simple tooltip with content prop
export { 
  TooltipRoot as Tooltip,  // Compound wrapper for <Tooltip><TooltipTrigger>...
  SimpleTooltip,           // Legacy: <SimpleTooltip content="...">
  TooltipProvider,
  TooltipTrigger,
  TooltipContent
};

// Default export is the simple tooltip for backward compat
export default SimpleTooltip;
